/**
 * /brands — 品牌综合口碑总表（顺手补的页，PR #11 范围之外的小增量）。
 *
 * 全部广汽品牌的横向对比表 + 跳转 BrandDetail。
 *
 * /api/v2/brands/weekly 后端没暴露 → 始终走 MOCK fallback + banner。
 * 一旦 Phase 5 加 list endpoint 即可切到真 API。
 */
import { useQuery } from '@tanstack/react-query';
import { Button, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import DataTable from '@/components/DataTable';
import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import RiskTag from '@/components/RiskTag';
import { api } from '@/lib/api';
import { useWeekStore } from '@/stores/weekStore';

interface BrandWeekly {
  brand_id: string;
  brand_name: string;
  brand_type: string;
  has_metrics: boolean; // 9.3-P0D-a: false = 无本周观测(启境/新车) → 显「暂无数据」非「低风险」
  official_nps_score: number | null;
  platform_avg_score: number | null;
  complaint_count_7d: number;
  complaint_count_30d: number;
  recall_count_ytd: number;
  sentiment_score: number | null;
  risk_score: number;
  risk_level: '低' | '中' | '高' | '重大' | null;
}

const MOCK: BrandWeekly[] = [
  {
    brand_id: 'GAC-AION',
    brand_name: '广汽埃安',
    brand_type: '新能源',
    has_metrics: true,
    official_nps_score: null,
    platform_avg_score: 3.8,
    complaint_count_7d: 487,
    complaint_count_30d: 1623,
    recall_count_ytd: 0,
    sentiment_score: -0.42,
    risk_score: 72,
    risk_level: '高',
  },
  {
    brand_id: 'GAC-TRUMP',
    brand_name: '广汽传祺',
    brand_type: '自主',
    has_metrics: true,
    official_nps_score: null,
    platform_avg_score: 4.0,
    complaint_count_7d: 312,
    complaint_count_30d: 1108,
    recall_count_ytd: 1,
    sentiment_score: -0.18,
    risk_score: 58,
    risk_level: '中',
  },
  {
    brand_id: 'GAC-HYPER',
    brand_name: '昊铂',
    brand_type: '高端新能源',
    has_metrics: true,
    official_nps_score: null,
    platform_avg_score: 4.2,
    complaint_count_7d: 38,
    complaint_count_30d: 121,
    recall_count_ytd: 0,
    sentiment_score: -0.09,
    risk_score: 41,
    risk_level: '中',
  },
  {
    brand_id: 'GAC-HONDA',
    brand_name: '广汽本田',
    brand_type: '合资',
    has_metrics: true,
    official_nps_score: null,
    platform_avg_score: 4.1,
    complaint_count_7d: 287,
    complaint_count_30d: 892,
    recall_count_ytd: 1,
    sentiment_score: -0.21,
    risk_score: 54,
    risk_level: '中',
  },
  {
    brand_id: 'GAC-TOYOTA',
    brand_name: '广汽丰田',
    brand_type: '合资',
    has_metrics: true,
    official_nps_score: null,
    platform_avg_score: 4.3,
    complaint_count_7d: 112,
    complaint_count_30d: 378,
    recall_count_ytd: 0,
    sentiment_score: 0.12,
    risk_score: 28,
    risk_level: '低',
  },
  {
    // 9.3-P0D-a: 新品牌无本周观测 → has_metrics=false, 显「暂无数据」非「低风险」
    brand_id: 'GAC-QIJING',
    brand_name: '启境',
    brand_type: '合资新能源',
    has_metrics: false,
    official_nps_score: null,
    platform_avg_score: null,
    complaint_count_7d: 0,
    complaint_count_30d: 0,
    recall_count_ytd: 0,
    sentiment_score: null,
    risk_score: 0,
    risk_level: null,
  },
];

export default function BrandsPage() {
  const navigate = useNavigate();
  const { week } = useWeekStore();

  const { data: apiData } = useQuery({
    queryKey: ['brands-weekly', week],
    queryFn: async () => {
      const res = await api.get<BrandWeekly[]>('/brands/weekly', { params: { week } });
      return res.data;
    },
    retry: false,
  });

  const data = apiData ?? MOCK;
  const usingMock = !apiData;

  const columns: ColumnsType<BrandWeekly> = [
    {
      title: '品牌',
      dataIndex: 'brand_name',
      width: 130,
      render: (n, row) => (
        <div>
          <div className="font-medium text-slate-900">{n}</div>
          <div className="text-[11px] text-slate-500">{row.brand_id}</div>
        </div>
      ),
    },
    {
      title: '业务属性',
      dataIndex: 'brand_type',
      width: 110,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    // 9.3-P0A: official_nps_score 为预留官方问卷 NPS 字段（当前未启用），不展示为有效指标 → 隐藏该列。
    {
      title: '平台综合口碑分',
      dataIndex: 'platform_avg_score',
      width: 110,
      render: (v: number | null) =>
        v != null ? (
          <span>
            <b>{v.toFixed(1)}</b> / 5
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: '近 7 天投诉',
      dataIndex: 'complaint_count_7d',
      width: 110,
      render: (v: number) => (
        <b className={v > 300 ? 'text-red-600' : v > 100 ? 'text-amber-600' : ''}>{v}</b>
      ),
    },
    { title: '近 30 天投诉', dataIndex: 'complaint_count_30d', width: 110 },
    { title: 'YTD 召回', dataIndex: 'recall_count_ytd', width: 90 },
    {
      title: '情绪分',
      dataIndex: 'sentiment_score',
      width: 90,
      render: (v: number | null) =>
        v == null ? (
          '—'
        ) : (
          <span
            className={
              v < -0.2 ? 'text-red-500' : v < 0 ? 'text-amber-500' : 'text-emerald-500'
            }
          >
            {v >= 0 ? '+' : ''}
            {v.toFixed(2)}
          </span>
        ),
    },
    {
      title: '风险等级',
      dataIndex: 'risk_level',
      width: 110,
      // 9.3-P0D-a J4: 无观测品牌(启境/新车) branch on has_metrics, 显「暂无数据」非假绿低风险
      render: (l: BrandWeekly['risk_level'], row) =>
        row.has_metrics && l ? (
          <RiskTag level={l} score={row.risk_score} />
        ) : (
          <span className="text-[12px] text-slate-400">暂无数据 · 待平台 ID</span>
        ),
    },
    {
      title: '操作',
      width: 80,
      render: (_, row) => (
        <a
          className="text-blue-600 cursor-pointer"
          onClick={() => navigate(`/brands/${row.brand_id}`)}
        >
          详情 →
        </a>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {usingMock && (
        <MockBanner message="当前显示模拟数据 · /api/v2/brands/weekly 列表 endpoint 待 Phase 5 暴露" />
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">品牌综合口碑总表</h1>
          <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
            PRD 模块一 · 整合平台综合口碑分、投诉、召回、情绪
          </p>
        </div>
        <Button icon={<DownloadOutlined />} className="self-start sm:self-auto">导出 Excel</Button>
      </div>
      <Panel title={`${data.length} 个品牌（${week}）`} noPadding>
        <div className="p-2 lg:p-0">
          <DataTable<BrandWeekly>
            rowKey="brand_id"
            columns={columns}
            data={data}
            pageSize={20}
            mobileMode="card"
            renderMobileCard={(row) => (
              <div
                className="flex flex-col gap-1.5"
                onClick={() => navigate(`/brands/${row.brand_id}`)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-900 text-[14px]">{row.brand_name}</div>
                    <div className="text-[11px] text-slate-500">{row.brand_id}</div>
                  </div>
                  {row.has_metrics && row.risk_level ? (
                    <RiskTag level={row.risk_level} score={row.risk_score} />
                  ) : (
                    <span className="text-[12px] text-slate-400">暂无数据 · 待平台 ID</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-600">
                  <span>平台综合口碑分 {row.platform_avg_score != null ? `${row.platform_avg_score.toFixed(1)}/5` : '—'}</span>
                  <span>情绪 {row.sentiment_score != null ? row.sentiment_score.toFixed(2) : '—'}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-600">
                  <span>7 天投诉 <b className={row.complaint_count_7d > 300 ? 'text-red-600' : ''}>{row.complaint_count_7d}</b></span>
                  <span>30 天 {row.complaint_count_30d}</span>
                  <span>YTD 召回 {row.recall_count_ytd}</span>
                </div>
              </div>
            )}
          />
        </div>
      </Panel>
    </div>
  );
}
